<template>
  <div class="subject-page container py-4">
    <!-- Alert -->
    <div v-if="alert.message" class="mb-3">
      <div
        :class="[
          'alert',
          alert.type === 'success' ? 'alert-success' : 'alert-danger',
        ]"
      >
        {{ alert.message }}
      </div>
    </div>

    <!-- Grade Dropdown -->
    <div class="mb-4">
      <label class="form-label">Select Grade</label>
      <select
        class="form-select grade-select"
        v-model="selectedGrade"
        @change="loadSubjects"
      >
        <option value="">All Grades</option>
        <option v-for="grade in grades" :key="grade.id" :value="grade.id">
          {{ grade.level }}
        </option>
      </select>
    </div>

    <!-- Loading Spinner -->
    <div v-if="loadingSubjects" class="text-center py-4">
      <div class="spinner-border text-pink" role="status"></div>
      <p class="mt-2 text-pink mb-0">Loading Subjects...</p>
    </div>

    <!-- No subjects message -->
    <div v-else-if="subjects.length === 0" class="text-center py-4 text-pink">
      No subjects available for this grade.
    </div>

    <!-- Subjects List -->
    <ul v-else class="list-group subject-list">
      <li
        v-for="subject in subjects"
        :key="subject.id"
        class="list-group-item d-flex justify-content-between align-items-center"
      >
        <!-- Editable input or static name -->
        <div class="flex-grow-1">
          <input
            v-if="editingSubjectId === subject.id"
            v-model="editingSubjectName"
            type="text"
            class="form-control subject-input"
          />
          <span v-else>{{ subject.name }}</span>
        </div>

        <!-- Buttons -->
        <div class="ms-2">
          <!-- Save button -->
          <button
            v-if="editingSubjectId === subject.id"
            class="btn btn-sm btn-success save-btn"
            :disabled="saving"
            @click="saveSubject(subject)"
          >
            <span v-if="saving">Saving...</span>
            <span v-else>Save</span>
          </button>

          <!-- Edit button -->
          <button
            v-else
            class="btn btn-sm btn-outline-primary edit-btn"
            @click="startEditing(subject)"
          >
            <i class="bi bi-pencil-fill"></i>
          </button>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      grades: [],
      subjects: [],
      selectedGrade: "",

      editingSubjectId: null,
      editingSubjectName: "",
      saving: false,

      alert: { type: "", message: "" },
      loadingSubjects: false,
    };
  },

  methods: {
    async loadGrades() {
      try {
        const res = await axios.get("/a-get-grades");
        this.grades = res.data;
      } catch (err) {
        console.error(err);
      }
    },

    async loadSubjects() {
      this.loadingSubjects = true;
      try {
        let url = "/a-get-subjects";
        if (this.selectedGrade)
          url = `/a-grade-subjects?grade_id=${this.selectedGrade}`;
        const res = await axios.get(url);
        this.subjects =
          res.data.subjects ||
          res.data.map((s) => ({
            id: s.subject?.id || s.id,
            name: s.subject?.name || s.name,
          }));
      } catch (err) {
        console.error(err);
        this.subjects = [];
      } finally {
        this.loadingSubjects = false;
      }
    },

    startEditing(subject) {
      this.editingSubjectId = subject.id;
      this.editingSubjectName = subject.name;
    },

    async saveSubject(subject) {
      if (!this.editingSubjectName.trim()) {
        this.showAlert("Subject name cannot be empty.", "error");
        return;
      }

      this.saving = true;

      try {
        const res = await axios.put(`/subjects/${subject.id}`, {
          name: this.editingSubjectName,
        });

        // Update subject in list
        const index = this.subjects.findIndex((s) => s.id === subject.id);
        if (index !== -1) this.subjects[index].name = res.data.subject.name;

        // Reset editing
        this.editingSubjectId = null;
        this.editingSubjectName = "";
        this.saving = false;

        this.showAlert("Subject updated successfully!", "success");
      } catch (err) {
        console.error(err);
        this.saving = false;
        this.showAlert("Failed to save subject.", "error");
      }
    },

    showAlert(message, type = "success") {
      this.alert.message = message;
      this.alert.type = type;

      setTimeout(() => {
        this.alert.message = "";
      }, 3000);
    },
  },

  mounted() {
    this.loadGrades();
    this.loadSubjects();
  },
};
</script>

<style scoped lang="scss">
.subject-page {
  .grade-select {
    background: #1a001f;
    color: #fff;
    border: 2px solid #ff00cc;
    border-radius: 8px;
    padding: 8px;
    transition: all 0.3s;

    &:focus {
      outline: none;
      box-shadow: 0 0 10px #ff00cc;
    }
  }

  .subject-list {
    .list-group-item {
      background: #1a001f;
      border: 2px solid #ff00cc;
      border-radius: 10px;
      margin-bottom: 10px;
      color: #fff;
      transition: all 0.3s;
      display: flex;
      align-items: center;

      &:hover {
        background: #2a002f;
      }

      .subject-input {
        background: #2a002f;
        color: #fff;
        border: 1px solid #ff00cc;
      }

      .edit-btn {
        border-color: #ff00cc;
        color: #ff00cc;
        transition: all 0.3s;

        &:hover {
          background: #ff00cc;
          color: #1a001f;
        }
      }

      .save-btn {
        background: #ff00cc;
        color: #1a001f;
        border: none;
        transition: all 0.3s;

        &:hover {
          background: #e600b3;
          color: #fff;
        }
      }
    }
  }

  .text-pink {
    color: #ff00cc;
  }

  .alert-success {
    background: #1a001f;
    color: #00ff99;
    border: 2px solid #00ff99;
  }

  .alert-danger {
    background: #1a001f;
    color: #ff4d4d;
    border: 2px solid #ff4d4d;
  }
}
</style>
